// ============================================================================
// Budgets — top summary row (CloudCost.dc.html `budgetStats` design).
// Presentational only: derives its four StatCards from data the Budgets screen
// already relies on (useGlobalBudget, useDepartments) plus the existing
// /reporting/costssummary endpoint. No new backend endpoints.
// ============================================================================
import { useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { StatCard, StatRow } from '@/components/ds';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import type { CostsSummary } from '@cloudcost/shared-types/costs';
import { useGlobalBudget } from './hooks/useGlobalBudget';
import { useDepartments } from './hooks/useDepartments';

const DASH = '—';

export default function BudgetStatsRow() {
  const { globalBudget } = useGlobalBudget();
  const { chartItems } = useDepartments();
  const [summary, setSummary] = useState<CostsSummary | null>(null);

  useEffect(() => {
    let active = true;
    fetchWrapper
      .get(urls.DevOps.costsSummary)
      .then((data: CostsSummary) => {
        if (active) setSummary(data);
      })
      .catch(() => {
        // Spent MTD / Forecast simply degrade to a dash if this call fails.
      });
    return () => {
      active = false;
    };
  }, []);

  const firstBox = summary?.firstBoxData?.data ?? {};
  const spentMtd = firstBox['Spending']?.value;
  const forecast = firstBox['Forecast']?.value;

  // Global budget is stored as a yearly limit; the card is a monthly figure.
  const globalMonthly =
    globalBudget?.original_currency_value != null
      ? globalBudget.original_currency_value / 12
      : undefined;

  // Count departments whose forecast (or MTD spend) breaches their monthly
  // budget, mirroring the design's "over" rule (forecast > budget).
  const deptsOverBudget = (chartItems as any[]).filter((d) => {
    const budget = Number(d?.budget_value ?? d?.value ?? 0);
    const fc = Number(d?.forecast ?? 0);
    const spent = Number(d?.spending ?? 0);
    return budget > 0 && (fc > budget || spent > budget);
  }).length;

  const money = (v?: number) =>
    v == null || Number.isNaN(v) ? DASH : formatCurrencyNoUnit(v);

  return (
    <StatRow columns={4}>
      <StatCard
        icon="budgets"
        tone="primary"
        label="Global budget (mo)"
        value={money(globalMonthly)}
      />
      <StatCard
        icon="dollar"
        tone="success"
        label="Spent MTD"
        value={money(spentMtd)}
      />
      <StatCard
        icon="spark"
        tone="warn"
        iconColor="#F28E2C"
        iconTint="#FDEFDE"
        label="Forecast (mo)"
        value={money(forecast)}
      />
      <StatCard
        icon="warn"
        tone="danger"
        label="Depts over budget"
        value={String(deptsOverBudget)}
      />
    </StatRow>
  );
}
